import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const JobPreview = ({ formData }) => {
  const getCategoryLabel = (value) => {
    const categories = {
      'plumbing': 'Plumbing',
      'electrical': 'Electrical',
      'carpentry': 'Carpentry',
      'cleaning': 'Cleaning',
      'babysitting': 'Babysitting',
      'tutoring': 'Tutoring',
      'driving': 'Driving',
      'cooking': 'Cooking',
      'laundry': 'Laundry',
      'errands': 'Errands',
      'tech-support': 'Tech Support',
      'beauty-services': 'Beauty Services',
    };
    return categories?.[value] || value;
  };

  const getTimelineLabel = (value) => {
    const timelines = {
      'urgent': 'Urgent (Within 24 hours)',
      'this-week': 'This Week',
      'this-month': 'This Month',
      'flexible': 'Flexible Timeline',
    };
    return timelines?.[value] || value;
  };

  const getPaymentTermsLabel = (value) => {
    const terms = {
      'upon-completion': 'Upon Completion',
      'milestone': 'Milestone-based',
      'upfront-50': '50% Upfront, 50% on Completion',
      'weekly': 'Weekly Payment',
    };
    return terms?.[value] || value;
  };

  const getVerificationLabel = (value) => {
    const levels = {
      'any': 'Any Worker',
      'basic': 'Basic Verified',
      'full': 'Fully Verified',
    };
    return levels?.[value] || value;
  };

  return (
    <div className="space-y-6">
      <div className="bg-accent/10 border border-accent/20 rounded-md p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Eye" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">Preview Your Job Post</h4>
            <p className="text-sm text-muted-foreground">
              This is how workers will see your job. Review carefully before publishing.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-md p-6 md:p-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              {formData?.title || 'Job Title'}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Tag" size={16} />
                <span>{getCategoryLabel(formData?.category) || 'Category'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} />
                <span>{getTimelineLabel(formData?.timeline) || 'Timeline'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>{formData?.city ? formData?.city?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ') : 'Location'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
            <Icon name="Circle" size={8} className="fill-current" />
            <span>Active</span>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">Budget</h3>
          <div className="flex items-center gap-4">
            {formData?.budgetType === 'hourly' ? (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl md:text-3xl font-bold text-primary">
                  ₱{formData?.hourlyRateMin || '0'} - ₱{formData?.hourlyRateMax || '0'}
                </span>
                <span className="text-muted-foreground">/hour</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl md:text-3xl font-bold text-primary">
                  ₱{formData?.fixedPrice || '0'}
                </span>
                <span className="text-muted-foreground">Fixed Price</span>
              </div>
            )}
            {formData?.negotiable && (
              <span className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm">
                Negotiable
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Payment: {getPaymentTermsLabel(formData?.paymentTerms) || 'Not specified'}
          </p>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">Description</h3>
          <p className="text-foreground whitespace-pre-line">
            {formData?.description || 'No description provided'}
          </p>
        </div>

        {formData?.workLocationType !== 'remote' && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Location Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Icon name="MapPin" size={16} className="mt-1 flex-shrink-0" color="var(--color-primary)" />
                <div>
                  <p className="font-medium text-foreground">{formData?.address || 'Address not provided'}</p>
                  <p className="text-muted-foreground">
                    {formData?.city ? formData?.city?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ') : 'City'}
                  </p>
                </div>
              </div>
              {formData?.landmark && (
                <div className="flex items-start gap-2">
                  <Icon name="Navigation" size={16} className="mt-1 flex-shrink-0" color="var(--color-primary)" />
                  <p className="text-muted-foreground">{formData?.landmark}</p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon name="Radius" size={16} color="var(--color-primary)" />
                <p className="text-muted-foreground">Workers within {formData?.radius || '10'} km</p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">Requirements</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} color="var(--color-primary)" />
              <span className="text-sm text-foreground">
                {getVerificationLabel(formData?.verificationLevel) || 'Any Worker'}
              </span>
            </div>
            {formData?.minExperience && (
              <div className="flex items-center gap-2">
                <Icon name="Award" size={16} color="var(--color-primary)" />
                <span className="text-sm text-foreground">
                  Minimum {formData?.minExperience} years experience
                </span>
              </div>
            )}
            {formData?.requiredSkills && formData?.requiredSkills?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Required Skills:</p>
                <div className="flex flex-wrap gap-2">
                  {formData?.requiredSkills?.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {formData?.specialInstructions && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Special Instructions</h3>
            <p className="text-sm text-foreground whitespace-pre-line">
              {formData?.specialInstructions}
            </p>
          </div>
        )}

        {formData?.images && formData?.images?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Reference Images</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {formData?.images?.map((image, index) => (
                <Image
                  key={index}
                  src={image?.url}
                  alt={image?.alt}
                  className="w-full h-32 md:h-40 object-cover rounded-md"
                />
              ))}
            </div>
          </div>
        )}

        <div className="pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <p className="font-medium text-foreground">Your Name</p>
                <p className="text-sm text-muted-foreground">Posted just now</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Eye" size={16} />
              <span>0 views</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPreview;